import { z } from 'zod'
import { ageOnRaceDay } from './format'
import { getEvent, race } from './race.config'

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA',
  'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR',
  'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
] as const

const trimmed = (max: number) => z.string().trim().max(max)
const required = (label: string, max = 80) => trimmed(max).min(1, `Enter ${label}`)
const phone = z
  .string()
  .trim()
  .refine((v) => v.replace(/\D/g, '').length >= 10, 'Enter a phone number with area code')

export const genders = [
  { id: 'female', label: 'Female' },
  { id: 'male', label: 'Male' },
  { id: 'nonbinary', label: 'Non-binary' },
] as const

export const participantSchema = z
  .object({
    eventId: z.enum(['5k'], { message: 'Choose an event' }).default('5k'),
    firstName: required('a first name', 40),
    lastName: required('a last name', 40),
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
    phone: z.string().trim().max(30).optional().default(''),
    dob: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a date of birth'),
    gender: z.enum(['female', 'male', 'nonbinary'], { message: 'Choose a division' }),
    city: required('a city', 60),
    state: z.enum(US_STATES, { message: 'Choose a state' }),
    shirtFit: z.enum(['unisex', 'womens', 'youth', 'none']),
    shirtSize: z.string().max(4).optional().default(''),
    showInList: z.boolean().default(false),
    emergencyName: required('an emergency contact name', 80),
    emergencyPhone: phone,
    guardianName: trimmed(80).optional().default(''),
    waiverSignature: trimmed(90).optional().default(''),
    waiverAccepted: z.boolean().default(false),
  })
  .superRefine((p, ctx) => {
    const event = getEvent(p.eventId)
    const dobValid = !Number.isNaN(new Date(p.dob).getTime())
    const age = dobValid ? ageOnRaceDay(p.dob) : NaN
    if (!dobValid || age < 0 || age > 110) {
      ctx.addIssue({ code: 'custom', path: ['dob'], message: 'Enter a real date of birth' })
    } else if (event) {
      if (event.minAge !== undefined && age < event.minAge)
        ctx.addIssue({ code: 'custom', path: ['dob'], message: `${event.name} is for ages ${event.minAge}+ on race day` })
      if (event.maxAge !== undefined && age > event.maxAge)
        ctx.addIssue({ code: 'custom', path: ['dob'], message: `${event.name} is for ages ${event.maxAge} and under on race day` })
    }
    if (p.shirtFit !== 'none') {
      const fit = race.shirts.fits.find((f) => f.id === p.shirtFit)
      if (!fit || !fit.sizes.includes(p.shirtSize))
        ctx.addIssue({ code: 'custom', path: ['shirtSize'], message: 'Choose a shirt size' })
    }
    if (dobValid && age < 18 && p.guardianName.length < 2)
      ctx.addIssue({ code: 'custom', path: ['guardianName'], message: 'A parent or guardian must sign for runners under 18' })
  })

export type ParticipantInput = z.input<typeof participantSchema>
export type Participant = z.output<typeof participantSchema>

export const extraShirtSchema = z.object({
  fit: z.enum(['unisex', 'womens', 'youth']),
  size: z.string().min(1).max(4),
  qty: z.number().int().min(1).max(10),
})

export const orderSchema = z
  .object({
    participants: z.array(participantSchema).min(1, 'Add at least one participant').max(10),
    extraShirts: z.array(extraShirtSchema).max(10).default([]),
    donationCents: z.number().int().min(0).max(1_000_000).default(0),
    couponCode: z.string().trim().toUpperCase().max(32).optional().default(''),
    purchaserEmail: z.string().trim().toLowerCase().email('Enter an email for your receipt'),
    marketingOptIn: z.boolean().default(false),
    acceptTerms: z.literal(true, { message: 'Please accept the refund policy and privacy notice' }),
  })
  .superRefine((o, ctx) => {
    o.participants.forEach((p, i) => {
      const signer = (ageOnRaceDay(p.dob) < 18 ? p.guardianName : `${p.firstName} ${p.lastName}`).trim().toLowerCase()
      if (!p.waiverAccepted)
        ctx.addIssue({ code: 'custom', path: ['participants', i, 'waiverAccepted'], message: 'The waiver must be accepted' })
      if (p.waiverSignature.trim().toLowerCase() !== signer)
        ctx.addIssue({
          code: 'custom',
          path: ['participants', i, 'waiverSignature'],
          message: 'Type the signer’s full name exactly as entered',
        })
    })
  })

export type OrderInput = z.input<typeof orderSchema>
export type Order = z.output<typeof orderSchema>

export const volunteerSchema = z.object({
  name: required('your name'),
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
  phone: z.string().trim().max(30).optional().default(''),
  roles: z.array(z.string()).min(1, 'Pick at least one role'),
  shirtSize: z.string().max(4).optional().default(''),
  notes: trimmed(500).optional().default(''),
})
export type VolunteerInput = z.input<typeof volunteerSchema>

/** "Email me when registration opens": just an address and, optionally, a first name. */
export const notifySchema = z.object({
  email: z.string().trim().toLowerCase().max(254).email('Enter a valid email address'),
  firstName: trimmed(60).optional().default(''),
  /** Honeypot: hidden from people, so anything in it came from a bot. */
  website: z.string().max(200).optional().default(''),
})
export type NotifyInput = z.input<typeof notifySchema>

/** A gift on the Donate page, made without registering. */
export const donationSchema = z.object({
  amountCents: z
    .number()
    .int()
    .min(500, 'The smallest gift is $5')
    .max(1_000_000, 'For gifts over $10,000 please email us'),
  /** Add the card fee on top so the full gift reaches the cause. */
  coverFee: z.boolean().default(true),
  name: trimmed(80).optional().default(''),
  email: z.string().trim().toLowerCase().email('Enter an email for your receipt'),
  /** Show the donor's name (never the amount) on the public supporters list. */
  showName: z.boolean().default(false),
})
export type DonationInput = z.input<typeof donationSchema>
export type Donation = z.output<typeof donationSchema>

export const volunteerRoles = [
  { id: 'packet', label: 'Packet pickup', when: 'Race morning, 7:00–9:00 AM' },
  { id: 'course', label: 'Course marshal', when: 'Race morning, 8:15–10:45 AM' },
  { id: 'water', label: 'Water station', when: 'Race morning, 8:15–10:45 AM' },
  { id: 'finish', label: 'Finish line & medals', when: 'Race morning, 8:30–11:00 AM' },
  { id: 'setup', label: 'Setup & teardown', when: 'Race morning, 6:30 AM or 10:30 AM' },
] as const

/** Flatten zod issues into { "participants.0.email": "message" } for form display. */
export function issuesToErrors(error: z.ZodError) {
  const out: Record<string, string> = {}
  for (const issue of error.issues) {
    const key = issue.path.join('.')
    if (!out[key]) out[key] = issue.message
  }
  return out
}
